/**
 * Instancia Axios compartida para la arquitectura por features.
 * Interceptors: token Bearer, headers authorization-role/resource, manejo de errores.
 */

import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import { config } from "common/config";

export type TokenGetter = () => Promise<string | null>;

/** Getter de token inyectable. La app lo configura en el bootstrap. */
let tokenGetter: TokenGetter | null = null;

export function setTokenGetter(getter: TokenGetter): void {
    tokenGetter = getter;
}

/** Obtiene el token actual (para EventSource, etc.). Usa el mismo getter que Axios. */
export async function getAccessToken(): Promise<string | null> { // skipcq: JS-0116
    return tokenGetter ? tokenGetter() : null;
}

import useAuthStore from "features/auth/store/authStore";

function getSelectedRole(): { roleType?: string; resourceAddress?: string } | undefined {
    return useAuthStore.getState().selectedRole;
}

const axiosInstance: AxiosInstance = axios.create({
    baseURL: config.backendUrl,
    withCredentials: true,
    headers: {
        Accept: "application/json",
    },
});

axiosInstance.interceptors.request.use(
    async (reqConfig: InternalAxiosRequestConfig) => {
        if (tokenGetter) {
            const token = await tokenGetter();
            if (token) {
                reqConfig.headers.Authorization = `Bearer ${token}`;
            }
        }

        const selectedRole = getSelectedRole();
        if (selectedRole?.roleType) {
            reqConfig.headers["authorization-role"] = selectedRole.roleType;
        }
        if (selectedRole?.resourceAddress) {
            reqConfig.headers["authorization-resource"] = selectedRole.resourceAddress;
        }

        return reqConfig;
    },
    (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // El refresh y logout se manejan en TanStack Query onError o en el componente.
            // Aquí solo re-lanzamos el error con formato compatible.
        }
        return Promise.reject(error);
    }
);

export default axiosInstance;
